import { useCallback, useEffect, useRef, useState } from "react";
import { Search, FileDown, Sheet, Loader2, Plus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, downloadBlob } from "@/lib/api";
import { Header } from "@/components/Header";
import { StatCards } from "@/components/StatCards";
import { Charts } from "@/components/Charts";
import { Ranking } from "@/components/Ranking";
import { Customers } from "@/components/Customers";
import { OrdersTable } from "@/components/orders/OrdersTable";
import { OrderDetail } from "@/components/orders/OrderDetail";
import { NewOrderForm } from "@/components/orders/NewOrderForm";
import { ProductionView } from "@/components/production/ProductionView";
import { CommissionSettings } from "@/components/CommissionSettings";
import { ProductsManager } from "@/components/ProductsManager";
import { Team } from "@/components/Team";
import { AuditLog } from "@/components/AuditLog";
import { Settlements } from "@/components/Settlements";
import { GalleryRealizations } from "@/components/GalleryRealizations";
import { AssistantWidget } from "@/components/AssistantWidget";
import { Procurement } from "@/components/production/Procurement";
import { Visualizations } from "@/components/Visualizations";
import { Drive } from "@/components/Drive";
import { ShopAdmin } from "@/components/shop/ShopAdmin";
import { B2bStores } from "@/components/b2b/B2bStores";
import { CrmView } from "@/components/crm/CrmView";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const STATUSES = ["Nowe zamówienie", "Wysłane do produkcji", "Zrealizowane", "Wysłane Garwolin", "Wysłane Nowy Sącz", "Wysłane do klienta", "Zamknięte", "Do weryfikacji"];

const TAB_DEFS = [
  { v: "orders", l: "Zamówienia" },
  { v: "customers", l: "Klienci" },
  { v: "crm", l: "CRM" },
  { v: "analytics", l: "Statystyki" },
  { v: "products", l: "Produkty" },
  { v: "gallery", l: "Galeria realizacji" },
  { v: "viz", l: "Wizualizacje" },
  { v: "drive", l: "Dysk EMPRO" },
  { v: "commissions", l: "Prowizje", emp: true },
  { v: "procurement", l: "Zaopatrzenie", admin: true },
  { v: "production", l: "Produkcja", admin: true },
  { v: "haft", l: "Haft", admin: true },
  { v: "shop", l: "Sklep", admin: true },
  { v: "b2b", l: "Sklepy B2B", admin: true },
  { v: "team", l: "Zespół", admin: true },
  { v: "settlements", l: "Rozliczenia", admin: true },
  { v: "audit", l: "Historia", admin: true },
  { v: "settings", l: "Ustawienia prowizji", admin: true },
];

export default function Dashboard() {
  const { user, roleView } = useAuth();
  const isDual = user.role === "admin" && user.dual_role;
  const isAdmin = user.role === "admin" && (!isDual || roleView === "admin");
  const showCommissions = !isAdmin && (user.role === "employee" || user.role === "admin");
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "orders");
  const tabRef = useRef(tab); tabRef.current = tab;
  const [catalog, setCatalog] = useState(null);
  const [users, setUsers] = useState([]);
  const [employee, setEmployee] = useState("all");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [custQuery, setCustQuery] = useState("");
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState(null);
  const [view, setView] = useState(null);
  const [editOrder, setEditOrder] = useState(null);
  const [presetCustomer, setPresetCustomer] = useState(null);
  const [ready, setReady] = useState(false);
  const vizRef = useRef(null);
  const histInit = useRef(true);
  const [pendingTab, setPendingTab] = useState(null);
  const [savingLeave, setSavingLeave] = useState(false);
  const [orderGroup, setOrderGroup] = useState("active");
  const [commissionPreset, setCommissionPreset] = useState("");
  const [datePreset, setDatePreset] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [dateBasis, setDateBasis] = useState("invoiced");

  const presetRange = (preset) => {
    const now = new Date();
    const y = now.getFullYear();
    const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    if (preset === "today") return [iso(now), iso(now)];
    if (preset === "month") return [iso(new Date(y, now.getMonth(), 1)), iso(new Date(y, now.getMonth() + 1, 0))];
    if (preset === "quarter") { const q = Math.floor(now.getMonth() / 3); return [iso(new Date(y, q * 3, 1)), iso(new Date(y, q * 3 + 3, 0))]; }
    if (preset === "year") return [`${y}-01-01`, `${y}-12-31`];
    return ["", ""];
  };
  const applyPreset = (preset) => {
    setDatePreset(preset);
    if (preset === "custom") return;
    const [f, t] = presetRange(preset);
    setDateFrom(f); setDateTo(t);
  };
  const periodLabel = (!dateFrom && !dateTo) ? "Cały okres" : `${dateFrom || "początek"} → ${dateTo || "teraz"}`;

  const requestTab = (v) => {
    if (v !== tab && tab === "viz" && vizRef.current?.isDirty?.()) { setPendingTab(v); return; }
    setTab(v);
  };
  const leaveSave = async () => {
    setSavingLeave(true);
    try { await vizRef.current?.save?.(); setTab(pendingTab); setPendingTab(null); }
    finally { setSavingLeave(false); }
  };
  const leaveDiscard = () => { setTab(pendingTab); setPendingTab(null); };

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("tab") !== tab) {
      p.set("tab", tab);
      const url = `${window.location.pathname}?${p.toString()}`;
      if (histInit.current) window.history.replaceState({ tab }, "", url);
      else window.history.pushState({ tab }, "", url);
    }
    histInit.current = false;
  }, [tab]);

  useEffect(() => {
    const onPop = () => {
      const t = new URLSearchParams(window.location.search).get("tab") || "orders";
      if (t !== "viz" && tabRef.current === "viz" && vizRef.current?.isDirty?.()) {
        const p = new URLSearchParams(window.location.search); p.set("tab", "viz");
        window.history.pushState({ tab: "viz" }, "", `${window.location.pathname}?${p.toString()}`);
        setPendingTab(t);
        return;
      }
      setTab(t);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const startEdit = (o) => { setEditOrder(o); setTab("new"); };
  const cancelEdit = () => { setEditOrder(null); setTab("orders"); };
  const startNewOrderFor = (c) => { setEditOrder(null); setPresetCustomer(c); setTab("new"); };

  const params = useCallback(() => {
    const p = {};
    if (isAdmin && employee !== "all") p.employee_id = employee;
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    p.date_basis = dateBasis;
    return p;
  }, [isAdmin, employee, dateFrom, dateTo, dateBasis]);

  const load = useCallback(async () => {
    const p = params();
    const { date_from, date_to, ...custP } = p;
    const [o, c, s] = await Promise.all([
      api.get("/orders", { params: { ...p, status: status === "all" ? undefined : status, q: q || undefined } }),
      api.get("/customers", { params: custP }),
      api.get("/stats", { params: p }),
    ]);
    setOrders(o.data); setCustomers(c.data); setStats(s.data);
    setReady(true);
  }, [params, status, q]);

  const loadCatalog = useCallback(() => api.get("/catalog").then((r) => setCatalog(r.data)), []);
  const loadUsers = useCallback(() => api.get("/users").then((r) => setUsers(r.data.filter((u) => u.role === "employee" || u.role === "admin"))), []);
  useEffect(() => { loadCatalog(); loadUsers(); }, [loadCatalog, loadUsers]);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const t = p.get("tab");
    const target = t === "comm-gaps" ? "settings" : t;
    const allowed = ["settings", "settlements", "orders", "products", "team"];
    if (target && allowed.includes(target)) setTab(target);
    const oid = p.get("order");
    if (oid) api.get(`/orders/${oid}`).then((r) => setView(r.data)).catch(() => {});
    if (oid) window.history.replaceState({}, "", window.location.pathname);
  }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { const id = setInterval(() => load(), 10000); return () => clearInterval(id); }, [load]);
  useEffect(() => {
    if (tab !== "new") setPresetCustomer(null);
    if (!isAdmin && ["production", "haft", "procurement", "team", "settlements", "audit", "settings", "comm-gaps", "b2b", "shop"].includes(tab)) setTab("orders");
  }, [isAdmin, tab]);

  const qs = () => { const p = new URLSearchParams(params()); if (status !== "all") p.set("status", status); const s = p.toString(); return s ? `?${s}` : ""; };

  return (
    <div className="min-h-screen relative z-10">
      {!ready && (
        <div data-testid="dashboard-loading" className="fixed inset-0 z-50 flex items-center justify-center bg-background/85 backdrop-blur-sm">
          <div className="flex items-center gap-3 rounded-xl border border-border bg-card px-5 py-3.5 text-sm font-semibold shadow-lg">
            <Loader2 className="h-5 w-5 animate-spin text-amber-400" />
            Ładowanie panelu…
          </div>
        </div>
      )}
      <Header />
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <Tabs value={tab} onValueChange={requestTab}>
          <div className="sticky top-16 z-30 space-y-2">
          <TabsList className="bg-card border border-border h-auto flex-wrap justify-start gap-1 p-1 w-full">
            {TAB_DEFS.filter((t) => (t.admin ? isAdmin : t.emp ? showCommissions : true)).flatMap((t) => {
              const trig = (
                <TabsTrigger key={t.v} asChild value={t.v} data-testid={`tab-${t.v}`}>
                  <a href={`?tab=${t.v}`} onClick={(e) => { if (e.metaKey || e.ctrlKey || e.button === 1) return; e.preventDefault(); if (t.v === "settings") setCommissionPreset(""); requestTab(t.v); }}
                    className={`cursor-pointer font-semibold uppercase text-xs tracking-wider ${t.v === "shop" ? "data-[state=active]:bg-sky-400 data-[state=active]:!text-[#0D1117] text-sky-300 ring-1 ring-sky-400/50 bg-sky-400/10" : "data-[state=active]:bg-amber-400 data-[state=active]:text-[#0D1117]"}`}>{t.l}</a>
                </TabsTrigger>
              );
              if (t.v === "orders" && editOrder) return [trig, (
                <TabsTrigger key="new" asChild value="new" data-testid="tab-new-order">
                  <a href="?tab=new" onClick={(e) => { if (e.metaKey || e.ctrlKey || e.button === 1) return; e.preventDefault(); requestTab("new"); }}
                    className="cursor-pointer data-[state=active]:bg-amber-400 data-[state=active]:text-[#0D1117] font-semibold uppercase text-xs tracking-wider">✏️ Zamówienie w edycji</a>
                </TabsTrigger>
              )];
              return [trig];
            })}
          </TabsList>
          {tab === "customers" && (
            <div className="relative" data-testid="customers-search-wrap">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input data-testid="customers-search" value={custQuery} onChange={(e) => setCustQuery(e.target.value)} placeholder="Szukaj klienta: nazwa, NIP, adres, osoba kontaktowa, telefon, e-mail…" className="pl-9 bg-card" />
            </div>
          )}
          </div>

          {tab !== "drive" && (<div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 rise mt-6">
            <div>
              <p className="font-mono text-xs text-amber-400 tracking-widest uppercase">{isAdmin ? "Panel administratora" : "Panel handlowca"}</p>
              <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight">{isAdmin ? "Cała firma" : user.name}</h1>
              <p data-testid="active-period-label" className="mt-1 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Okres: <span className="text-foreground font-semibold">{periodLabel}</span></p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={datePreset} onValueChange={applyPreset}>
                <SelectTrigger data-testid="date-preset-filter" className="w-[168px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Cały okres</SelectItem>
                  <SelectItem value="today">Dzisiaj</SelectItem>
                  <SelectItem value="month">Ten miesiąc</SelectItem>
                  <SelectItem value="quarter">Ten kwartał</SelectItem>
                  <SelectItem value="year">Ten rok</SelectItem>
                  <SelectItem value="custom">Zakres dat…</SelectItem>
                </SelectContent>
              </Select>
              {datePreset === "custom" && (
                <>
                  <Input data-testid="date-from" type="date" value={dateFrom} max={dateTo || undefined} onChange={(e) => setDateFrom(e.target.value)} className="w-[150px] bg-card" />
                  <span className="text-muted-foreground">—</span>
                  <Input data-testid="date-to" type="date" value={dateTo} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)} className="w-[150px] bg-card" />
                </>
              )}
              {(dateFrom || dateTo) && (
                <Button data-testid="date-clear" variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground" onClick={() => applyPreset("all")}>Wyczyść</Button>
              )}
              <Select value={dateBasis} onValueChange={setDateBasis}>
                <SelectTrigger data-testid="date-basis-filter" className="w-[180px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="invoiced">Wg daty faktury</SelectItem>
                  <SelectItem value="created">Wg daty dodania</SelectItem>
                </SelectContent>
              </Select>
              {isAdmin && (
                <Select value={employee} onValueChange={setEmployee}>
                  <SelectTrigger data-testid="employee-filter" className="w-[220px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Wszyscy handlowcy</SelectItem>
                    {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>)}

          {tab !== "drive" && <><div className="mt-1" /><StatCards stats={stats} isAdmin={isAdmin} /></>}

          <TabsContent value="orders" className="space-y-4 mt-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input data-testid="orders-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Szukaj: klient, NIP, adres, numer…" className="pl-9" />
              </div>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="status-filter" className="sm:w-[180px]"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">Wszystkie statusy</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              <Button data-testid="add-order-button" className="gap-2 bg-amber-400 text-[#0D1117] hover:bg-amber-300 font-semibold uppercase tracking-wider" onClick={() => { setEditOrder(null); setPresetCustomer(null); setTab("new"); }}><Plus className="h-4 w-4" />Dodaj zamówienie</Button>
              <Button data-testid="export-csv-button" variant="outline" className="gap-2" onClick={() => downloadBlob(`/orders/export/csv${qs()}`, "zamowienia.csv")}><Sheet className="h-4 w-4" />CSV</Button>
              <Button data-testid="export-pdf-button" variant="outline" className="gap-2" onClick={() => downloadBlob(`/orders/export/pdf${qs()}`, "zamowienia.pdf")}><FileDown className="h-4 w-4" />PDF</Button>
            </div>
            {(() => {
              const verify = orders.filter((o) => o.status === "Do weryfikacji" && !o.invoiced);
              const invoiced = orders.filter((o) => o.invoiced);
              const active = orders.filter((o) => !o.invoiced && ["Nowe zamówienie", "Wysłane do produkcji"].includes(o.status));
              const done = orders.filter((o) => !o.invoiced && !["Nowe zamówienie", "Wysłane do produkcji", "Do weryfikacji"].includes(o.status));
              const list = orderGroup === "invoiced" ? invoiced : orderGroup === "done" ? done : orderGroup === "verify" ? verify : active;
              return (
                <>
                  <div className="flex flex-wrap gap-1 rounded-lg border border-border bg-card p-1 w-fit max-w-full" data-testid="orders-group-toggle">
                    <button data-testid="orders-group-active" onClick={() => setOrderGroup("active")} className={`rounded-md px-3 sm:px-4 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${orderGroup === "active" ? "bg-amber-400 text-[#0D1117]" : "text-muted-foreground hover:text-foreground"}`}>W trakcie ({active.length})</button>
                    <button data-testid="orders-group-done" onClick={() => setOrderGroup("done")} className={`rounded-md px-3 sm:px-4 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${orderGroup === "done" ? "bg-amber-400 text-[#0D1117]" : "text-muted-foreground hover:text-foreground"}`}>Zrealizowane ({done.length})</button>
                    <button data-testid="orders-group-invoiced" onClick={() => setOrderGroup("invoiced")} className={`rounded-md px-3 sm:px-4 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${orderGroup === "invoiced" ? "bg-amber-400 text-[#0D1117]" : "text-muted-foreground hover:text-foreground"}`}>Zafakturowane ({invoiced.length})</button>
                    <button data-testid="orders-group-verify" onClick={() => setOrderGroup("verify")} className={`rounded-md px-3 sm:px-4 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${orderGroup === "verify" ? "bg-amber-500 text-[#0D1117]" : verify.length ? "text-amber-300 ring-1 ring-amber-500/50 hover:text-amber-200" : "text-muted-foreground hover:text-foreground"}`}>Do weryfikacji ({verify.length})</button>
                  </div>
                  <OrdersTable orders={list} isAdmin={isAdmin} onChanged={load} onView={setView} onEdit={startEdit} />
                </>
              );
            })()}
          </TabsContent>

          {<TabsContent value="new" className="mt-4">
            {catalog && <NewOrderForm key={editOrder?.id || presetCustomer?.id || presetCustomer?.productSku || "new"} catalog={catalog} customers={customers} users={users} isAdmin={isAdmin} editOrder={editOrder} presetCustomerId={presetCustomer?.id} presetOwnerId={presetCustomer?.employee_id} presetProductSku={presetCustomer?.productSku} onCancelEdit={cancelEdit} onCustomersChanged={load} onCreated={() => { load(); setEditOrder(null); setPresetCustomer(null); setTab("orders"); }} />}
          </TabsContent>}

          <TabsContent value="customers" className="mt-4">
            <Customers customers={customers} orders={orders} isAdmin={isAdmin} users={users} query={custQuery} dateFrom={dateFrom} dateTo={dateTo} onChanged={load} onNewOrder={startNewOrderFor} onEditViz={(vizId) => { setTab("viz"); setTimeout(() => vizRef.current?.openVizById?.(vizId), 80); }} />
          </TabsContent>

          <TabsContent value="crm" className="mt-4">
            <CrmView isAdmin={isAdmin} customers={customers} users={users} dateFrom={dateFrom} dateTo={dateTo} onCustomersChanged={load} />
          </TabsContent>

          <TabsContent value="analytics" className="space-y-4 mt-4">
            <Charts stats={stats} />
            <Ranking basis={dateBasis} onPick={isAdmin ? (id) => setEmployee(id) : undefined} />
          </TabsContent>

          <TabsContent value="products" className="mt-4">
            <ProductsManager onSaved={loadCatalog} isAdmin={isAdmin} />
          </TabsContent>

          <TabsContent value="gallery" className="mt-4">
            <GalleryRealizations />
          </TabsContent>

          <TabsContent value="viz" className="mt-4 data-[state=inactive]:hidden" forceMount>
            <Visualizations ref={vizRef} isAdmin={isAdmin} />
          </TabsContent>

          <TabsContent value="drive" className="mt-4">
            <Drive />
          </TabsContent>

          {isAdmin && <TabsContent value="procurement" className="mt-4">
            <Procurement />
          </TabsContent>}

          {isAdmin && <TabsContent value="production" className="mt-4">
            <ProductionView isAdmin={isAdmin} />
          </TabsContent>}

          {isAdmin && <TabsContent value="haft" className="mt-4">
            <ProductionView base="/haft" canStatus={false} pdfBase="/haft" emptyText="Brak zamówień z haftem." />
          </TabsContent>}

          {isAdmin && <TabsContent value="shop" className="mt-4">
            <ShopAdmin />
          </TabsContent>}
          {isAdmin && <TabsContent value="b2b" className="mt-4">
            <B2bStores />
          </TabsContent>}

          {isAdmin && <TabsContent value="team" className="mt-4">
            <Team onChanged={loadUsers} />
          </TabsContent>}

          {isAdmin && <TabsContent value="settlements" className="mt-4">
            <Settlements />
          </TabsContent>}

          {showCommissions && <TabsContent value="commissions" className="mt-4">
            <Settlements mine />
          </TabsContent>}

          {isAdmin && <TabsContent value="audit" className="mt-4">
            <AuditLog />
          </TabsContent>}

          {isAdmin && <TabsContent value="settings" className="mt-4">
            <CommissionSettings onSaved={loadCatalog} initialQuery={commissionPreset} onOpenOrder={(id) => api.get(`/orders/${id}`).then((r) => setView(r.data)).catch(() => {})} />
          </TabsContent>}
        </Tabs>
      </main>
      <AssistantWidget onStartOrder={(oi) => startNewOrderFor({ id: oi.customer_id, customer_name: oi.customer_name, employee_id: oi.employee_id, productSku: oi.product_sku })} />
      <OrderDetail order={view} onClose={() => setView(null)} isAdmin={isAdmin} onDeleted={() => { setView(null); load(); }} />
      <AlertDialog open={!!pendingTab} onOpenChange={(o) => { if (!o) setPendingTab(null); }}>
        <AlertDialogContent data-testid="viz-leave-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Opuścić kreator bez zapisu?</AlertDialogTitle>
            <AlertDialogDescription>Masz niezapisane zmiany w wizualizacji. Czy na pewno chcesz opuścić kreator? Niezapisane zmiany zostaną utracone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="viz-leave-cancel" disabled={savingLeave}>Anuluj</AlertDialogCancel>
            <AlertDialogAction data-testid="viz-leave-discard" onClick={leaveDiscard} disabled={savingLeave} className="bg-secondary text-foreground hover:bg-secondary/80">Wyjdź bez zapisu</AlertDialogAction>
            <AlertDialogAction data-testid="viz-leave-save" onClick={(e) => { e.preventDefault(); leaveSave(); }} disabled={savingLeave} className="bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold">{savingLeave ? "Zapisywanie…" : "Zapisz projekt"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
